'use client';
import { useState, useTransition, type JSX } from 'react';
import { Button } from '@/components/ui/button';
import { HiArrowTurnRightDown } from 'react-icons/hi2';
import { useLocale } from 'next-intl';
import { getMediaSrcAndAlt } from '@/lib/functions';
import { Link } from '@/i18n/navigation';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import type { PaginatedDocs } from 'payload';
import type { GeneralPost } from '@/payload-types';

export function MorePostList({
  take,
  isButtonHidden,
  tagSlugs,
}: {
  take: number;
  isButtonHidden: boolean;
  tagSlugs?: string[];
}): JSX.Element {
  const [isButtonVisible, setIsButtonVisible] = useState<boolean>(isButtonHidden);
  const [pageNumber, setPageNumber] = useState<number>(2);
  const [posts, setPosts] = useState<
    PaginatedDocs<GeneralPost & { imageUrl?: string; imageAlt?: string }>['docs']
  >([]);
  const [isPending, startTransition] = useTransition();
  const locale = useLocale() as Locales;

  async function loadMoreToggle(): Promise<void> {
    const searchParams = new URLSearchParams();
    searchParams.set('page', pageNumber.toString());
    searchParams.set('limit', take.toString());
    searchParams.set('locale', locale);
    if (tagSlugs && tagSlugs.length > 0) {
      searchParams.set('where[tags.slug][in]', tagSlugs.join(','));
    }
    const response = await fetch(`/api/general-posts?${searchParams.toString()}`);

    if (response.ok) {
      const data = (await response.json()) as PaginatedDocs<
        GeneralPost & { imageUrl?: string; imageAlt?: string }
      >;
      for (const post of data.docs) {
        const media = await getMediaSrcAndAlt(post.image);
        if (media) {
          post.imageUrl = media.src;
          post.imageAlt = media.alt;
        }
      }
      setPosts((prevPosts) => [...prevPosts, ...data.docs]);
      setPageNumber((prevPage) => prevPage + 1);
      setIsButtonVisible(data.hasNextPage);
    }
  }

  return (
    <>
      {posts.length > 0 &&
        posts.map((post, index) => (
          <article
            // biome-ignore lint/suspicious/noArrayIndexKey: 「もっと見る」で末尾に追記するのみ。既存要素の位置は変わらない
            key={index}
            className="w-full border-b border-dotted border-b-stone-300 dark:border-b-stone-700"
          >
            <Link
              href={`/article/${post.slug}`}
              className="flex flex-col sm:flex-row items-start p-2 transition-colors hover:bg-stone-200 dark:hover:bg-stone-800"
            >
              <div className="w-full sm:w-1/4 aspect-video flex items-center justify-center relative bg-muted">
                {post.imageUrl ? (
                  <Image
                    src={post.imageUrl}
                    alt={
                      post.imageAlt || (locale === 'ja' ? '"サムネイル画像"' : 'thumbnail Image')
                    }
                    layout="fill"
                    objectFit="cover"
                  />
                ) : (
                  <span className="text-xl text-muted-foreground">No Image</span>
                )}
              </div>
              <div className="w-full sm:w-3/4 pl-2 sm:pl-4 pr-2 py-2 flex flex-col">
                <h3 title={post.title} className="text-lg font-semibold line-clamp-2 mb-2">
                  {post.title}
                </h3>
                <p className="text-sm text-muted-foreground line-clamp-4">{post.excerpt}</p>
              </div>
            </Link>
          </article>
        ))}
      {isButtonVisible && (
        <div className="flex items-center justify-center py-4">
          <Button
            type="button"
            variant={'secondary'}
            className="!px-7"
            aria-label={locale === 'ja' ? 'もっと見る' : 'Load More'}
            onClick={() => startTransition(loadMoreToggle)}
            disabled={isPending || !isButtonVisible}
          >
            {isPending && (
              <span
                className={cn([
                  'animate-spin size-[1.2em] block transition-opacity',
                  'rounded-full border-4 border-solid',
                  'border-slate-700 dark:border-slate-500 !border-t-transparent',
                ])}
              ></span>
            )}
            {locale === 'ja' ? 'もっと見る' : 'Load More'}
            <HiArrowTurnRightDown />
          </Button>
        </div>
      )}
    </>
  );
}
